import type { IOption } from './nestedSelectTypes';
import { flattenOptions } from './utilities';

/**
 * Zamienia stan zaznaczenia z NestedSelect na listę tekstów pokazywanych jako chipy
 * oraz (w drugiej funkcji) na listę numerycznych id do wysłania w żądaniu.
 *
 * Idea: drzewo opcji może mieć kilka poziomów. Jeśli zaznaczone są wszystkie liście pod
 * jakąś gałęzią, pokazujemy jedną etykietę na poziomie tej gałęzi (wysoka agregacja).
 * Jeśli tylko część poddrzewa jest zaznaczona, schodzimy głębiej — widać konkretniejsze
 * pozycje albo same liście.
 *
 * Reguła „czy wszystkie liście są zaznaczone” musi być taka sama przy budowie chipów
 * i przy budowie body uprawnień do obszarów, stąd wspólny warunek branchHasAllLeavesSelected.
 */

/** Czy dana wartość (zwykle id liścia jako string) znajduje się w bieżącym zaznaczeniu. */
function buildSelectionContains(selectedOptions: IOption[]) {
  const flatSelected = flattenOptions(selectedOptions);
  return (leafValue: string | undefined) =>
    flatSelected.some((s) => s.value === leafValue);
}

/**
 * Dla węzła będącego grupą (ma `options`): czy każda liść w jego poddrzewie — liczona
 * rekurencyjnie przez flattenOptions — jest w zaznaczeniu?
 * Pusta tablica `options: []` oznacza grupę bez dzieci; wtedy `every` na pustej liście
 * daje true (tak samo zachowuje się select w interfejsie przy chipach).
 */
function branchHasAllLeavesSelected(
  option: IOption,
  contains: (leafValue: string | undefined) => boolean
): boolean {
  if (!option.options) return false;
  return flattenOptions(option.options).every((item) => contains(item.value));
}

/** Teksty do chipów przy zamkniętym polu selectu (materialized / selectedLabel / label). */
export function parseNestedSelectSelectedLabels(
  parsingOptions: IOption[],
  selectedOptions: IOption[]
): string[] {
  const contains = buildSelectionContains(selectedOptions);

  const walk = (nodes: IOption[]): string[] =>
    (nodes
      .map((option) =>
        option.options
          ? branchHasAllLeavesSelected(option, contains)
            ? option.selectedLabel ?? option.label
            : walk(option.options)
          : contains(option.value)
            ? option.selectedLabel ?? option.label
            : null
      )
      .flat(Infinity)
      .filter((item) => item) || []) as string[];

  return walk(parsingOptions);
}

/**
 * Ta sama agregacja co przy chipach, ale zamiast stringów zwracamy id obszarów pod API.
 *
 * Gdy zwój obejmuje całą gałąź, wystarczy jedno `area_id` rodzica — backend i tak potrafi
 * rozliczyć zakres. Gdy zaznaczenie jest częściowe, dostajemy numery liści (albo
 * niższych gałęzi po dalszym schodzeniu).
 *
 * Gałąź bez sensownego `value` (nie da się z niej zrobić liczby) nie może reprezentować
 * wpisu w payloadzie — wtedy schodzimy do dzieci i zbieramy id stamtąd. Przy samych chipach
 * taki syntetyczny węzeł nadal pokazuje etykietę z rodzica; stąd różnica względem
 * parseNestedSelectSelectedLabels tylko w tym wyjątku.
 */
export function collapseSelectedAreaPermissionIds(
  parsingOptions: IOption[],
  selectedOptions: IOption[]
): number[] {
  const contains = buildSelectionContains(selectedOptions);

  const walkIds = (nodes: IOption[]): number[] =>
    nodes.flatMap((option) =>
      option.options
        ? branchHasAllLeavesSelected(option, contains)
          ? (() => {
            const n = Number(option.value);
            return Number.isNaN(n) ? walkIds(option.options) : [n];
          })()
          : walkIds(option.options)
        : contains(option.value)
          ? (() => {
            const n = Number(option.value);
            return Number.isNaN(n) ? [] : [n];
          })()
          : []
    );

  return Array.from(new Set(walkIds(parsingOptions)));
}
